/**
 * Runtime side of the geofence (#769): fetch /geo.json, remember terms acceptance. Logic lives in policy.ts.
 */
import policyJson from '../../../shared/policy/jurisdictions.json';
import termsJson from '../../../shared/policy/terms.json';
import { parsePolicy, parseGeoResponse, parseTerms, type GeoLocation } from './policy';

/** The bundled jurisdiction policy (validated at import, so a typo breaks the build's tests, not production). */
export const POLICY = parsePolicy(policyJson);
/** The bundled terms of use. */
export const TERMS = parseTerms(termsJson);

/** Same-origin endpoint answered by the CloudFront Function (deploy/cloudfront/). */
export const GEO_URL = '/geo.json';
/** Give up on one /geo.json request after this long (the policy's `unknown` mode applies meanwhile). */
export const GEO_TIMEOUT_MS = 3_000;
/**
 * After an unknown answer, ask again this many times, GEO_RETRY_DELAY_MS apart, then stay on `unknown` until a
 * reload. Same cadence as Hyperliquid's own frontend (legalCheck: 3 retries, 2 s apart).
 */
export const GEO_RETRIES = 3;
export const GEO_RETRY_DELAY_MS = 2_000;

export type GeoLookup =
  | { ok: true; location: GeoLocation }
  | { ok: false; reason: string };

/**
 * Ask /geo.json where the viewer is. Never throws: every failure is `{ ok: false, reason }` for the caller to log.
 * A `country: null` answer (CloudFront could not place the viewer) is ok with a null country.
 */
export async function lookupGeo(
  fetchImpl: typeof fetch = (...a) => fetch(...a),
  timeoutMs: number = GEO_TIMEOUT_MS,
): Promise<GeoLookup> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<GeoLookup>((resolve) => {
    timer = setTimeout(() => {
      ctrl?.abort();
      resolve({ ok: false, reason: `timeout after ${timeoutMs} ms` });
    }, timeoutMs);
  });
  const attempt = (async (): Promise<GeoLookup> => {
    try {
      const r = await fetchImpl(GEO_URL, { cache: 'no-store', credentials: 'omit', signal: ctrl?.signal });
      if (!r.ok) return { ok: false, reason: `HTTP ${r.status}` };
      const loc = parseGeoResponse(await r.text());
      if (!loc) return { ok: false, reason: 'not a geo response (function not attached?)' };
      return { ok: true, location: loc };
    } catch (e) {
      return { ok: false, reason: e instanceof Error ? e.message : String(e) };
    }
  })();
  try {
    return await Promise.race([attempt, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Terms acceptance storage. localStorage when available; every access is wrapped because storage can be missing or
// throw (private mode, blocked site data). Without storage an in-memory copy keeps the gate closed for the session.

export const TERMS_STORAGE_KEY = 'outfox.terms';

type Store = Pick<Storage, 'getItem' | 'setItem'>;

let memory: string | null = null;

function defaultStore(): Store | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadAcceptanceRaw(store: Store | undefined = defaultStore()): string | null {
  try {
    return store?.getItem(TERMS_STORAGE_KEY) ?? memory;
  } catch {
    return memory;
  }
}

export function saveAcceptanceRaw(raw: string, store: Store | undefined = defaultStore()): void {
  memory = raw;
  try {
    store?.setItem(TERMS_STORAGE_KEY, raw);
  } catch {
    /* storage unavailable: the in-memory copy still applies for this session */
  }
}

/** Test hook: forget the in-memory fallback. */
export function resetTermsMemory(): void {
  memory = null;
}
